import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { cn } from '../utils/cn';
import {
  LayoutDashboard,
  FileText,
  PenTool,
  Users,
  BarChart3,
  Bell,
  Settings,
  Mail,
  Menu,
  X,
} from 'lucide-react';
import { NotificationBadge } from './notifications/NotificationBadge';
import { NotificationsPanel } from './notifications/NotificationsPanel';
import { SettingsModal } from './settings/SettingsModal';
import { useNotificationStore } from '../stores/notificationStore';
import { useUIStore } from '../stores/uiStore';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import { useTheme } from '../hooks/useTheme';
import { UserProfileMenu } from './profile/UserProfileMenu';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, shortcut: 'Alt + D' },
  { name: 'Contracts', href: '/contracts', icon: FileText, shortcut: 'Alt + C' },
  { name: 'Proposals', href: '/proposals', icon: PenTool, shortcut: 'Alt + P' },
  { name: 'Marketplace', href: '/marketplace', icon: Users, shortcut: 'Alt + M' },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, shortcut: 'Alt + A' },
  { name: 'Contact', href: '/contact', icon: Mail },
];

export function Layout() {
  const location = useLocation();
  const { notifications, markAsRead, markAllAsRead } = useNotificationStore();
  const { theme, setTheme } = useTheme();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Initialize keyboard navigation
  useKeyboardNavigation();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <div className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 transform bg-white transition-transform duration-300 ease-in-out dark:bg-gray-800 lg:static lg:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}>
          {/* Sidebar content */}
          <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex flex-shrink-0 items-center px-4 py-5">
              <Link to="/" className="flex items-center space-x-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-600">
                  <img src="/logo.svg" alt="Bidzora" className="h-8 w-8" />
                </div>
                <h1 className="text-2xl font-bold text-purple-600">Bidzora</h1>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 px-2 py-4">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'group flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/50 dark:text-purple-300'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    )}
                  >
                    <item.icon className={cn(
                      'mr-3 h-5 w-5',
                      isActive ? 'text-purple-600 dark:text-purple-300' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                    )} />
                    {item.name}
                    {item.shortcut && (
                      <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                        {item.shortcut}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User Profile Menu - Positioned at bottom */}
            <div className="mt-auto border-t border-gray-200 dark:border-gray-700">
              <UserProfileMenu />
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Top header */}
          <header className="w-full">
            <div className="relative z-10 flex h-16 flex-shrink-0 border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex flex-1 justify-between px-4 sm:px-6">
                {/* Mobile menu button */}
                <div className="flex items-center lg:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    {isMobileMenuOpen ? (
                      <X className="h-6 w-6" />
                    ) : (
                      <Menu className="h-6 w-6" />
                    )}
                  </button>
                </div>

                {/* Search bar */}
                <div className="flex flex-1 items-center px-4">
                  <input
                    type="text"
                    placeholder="Search contracts, proposals..."
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>

                {/* Right side buttons */}
                <div className="flex items-center space-x-4">
                  <NotificationBadge
                    notifications={notifications}
                    onOpen={() => setShowNotifications(true)}
                  />
                  <button
                    onClick={() => setShowSettings(true)}
                    className="rounded-full p-1 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <Settings className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Main content area */}
          <main className="flex-1 overflow-y-auto bg-gray-50 p-6 dark:bg-gray-900">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Modals */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
      />
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}